
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteFeedPosts } from '../hooks/usePosts';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { Skeleton } from '@/components/ui/skeleton';
import PostCard from '@/components/PostCard';
import PetFriendlyPlacesBanner from '@/components/places/PetFriendlyPlacesBanner';
import { RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';

const Feed = () => {
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = useInfiniteFeedPosts();
  
  const [refreshing, setRefreshing] = useState(false);
  const { t } = useTranslation();
  
  // Flatten all pages into a single array of posts
  const posts = data?.pages.flat() || [];

  // ✅ FIX: Log safe areas on mount (removed resize event to prevent infinite loop)
  useEffect(() => {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      setTimeout(() => {
        const computedStyle = getComputedStyle(document.documentElement);
        console.log('🔄 Feed mounted - Safe areas:', {
          top: computedStyle.getPropertyValue('--safe-area-inset-top'),
          bottom: computedStyle.getPropertyValue('--safe-area-inset-bottom')
        });
      }, 50);
    }
  }, []);

  // Enable infinite scroll
  useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    threshold: 500 // Start loading when 500px from bottom
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    // Save current scroll position
    const scrollPosition = window.scrollY;
    await refetch();
    setTimeout(() => {
      setRefreshing(false);
      // Restore scroll position after refresh
      window.scrollTo(0, scrollPosition);
    }, 300);
  };

  if (error) {
    return (
      <div className="container mx-auto max-w-2xl p-4 py-8">
        <div className="text-center py-12">
          <p className="text-red-500 mb-4">{t('common.error')} loading posts</p>
          <Button 
            variant="outline"
            onClick={handleRefresh}
            className="flex items-center"
          >
            <RefreshCw className="mr-2 h-4 w-4" /> {t('common.tryAgain')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl md:p-4 pt-4">
      <div className="mb-3 flex items-center justify-end px-4 md:px-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRefresh}
          disabled={isLoading || refreshing}
          className="flex items-center"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${(isLoading || refreshing) ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="space-y-4 md:space-y-6">
        {isLoading || refreshing ? (
          // Enhanced loading skeletons for better UX
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="rounded-lg overflow-hidden border bg-card text-card-foreground shadow-sm animate-pulse">
              <div className="p-4 flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
              <Skeleton className="h-72 w-full" />
              <div className="p-4 space-y-3">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <div className="flex gap-4 pt-2">
                  <Skeleton className="h-8 w-16" />
                  <Skeleton className="h-8 w-16" />
                  <Skeleton className="h-8 w-16" />
                </div>
              </div>
            </div>
          ))
        ) : posts && posts.length > 0 ? (
          <>
            {(() => {
              const visited = typeof window !== 'undefined' && localStorage.getItem('pf_places_visited') === '1';
              const alreadyShown = typeof window !== 'undefined' && sessionStorage.getItem('pf_places_feed_shown') === '1';
              const showBanner = !visited && !alreadyShown && posts.length >= 3;
              const insertAt = Math.min(10, Math.max(2, Math.floor(posts.length / 2)));
              if (showBanner && typeof window !== 'undefined') {
                sessionStorage.setItem('pf_places_feed_shown', '1');
              }
              const nodes: React.ReactNode[] = [];
              posts.forEach((post, idx) => {
                nodes.push(<PostCard key={post.id} post={post} />);
                if (showBanner && idx === insertAt) {
                  nodes.push(<PetFriendlyPlacesBanner key="pf-places-banner" />);
                }
              });
              return nodes;
            })()}
            
            
            
            {/* Subtle loading indicator for infinite scroll */}
            {isFetchingNextPage && (
              <div className="flex justify-center py-8">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="text-sm">Loading more posts...</span>
                </div>
              </div>
            )}
            
            {/* End of posts indicator */}
            {!hasNextPage && posts.length > 10 && (
              <div className="text-center py-8">
                <div className="text-muted-foreground text-sm">
                  🐾 You've reached the end! That's all for now.
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16 bg-gray-50 rounded-lg">
            <h3 className="text-xl font-medium mb-2">Your feed is empty</h3>
            <p className="text-gray-500 mb-6">
              Posts from pets you follow will appear here. Start by adding your pet and creating memories!
            </p>
            <div className="flex justify-center gap-4 flex-wrap">
              <Link to="/my-pets">
                <Button>Add Your Pet</Button>
              </Link>
              <Link to="/create-post">
                <Button variant="outline">Create a Memory</Button>
              </Link>
            </div>
          </div>
        )}
      </div>
      
      {/* Bottom spacer for mobile navigation */}
      <div className="h-20 md:h-0" aria-hidden="true"></div>
    </div>
  );
};

export default Feed;
